// FILE: user-location.js

let userMarker;
const MILES_TO_METERS = 1609.34;
const MAX_DISTANCE = 150 * MILES_TO_METERS; // 100 miles in meters
const INITIAL_DISTANCE = 150 * MILES_TO_METERS; // 100 miles in meters
const EXPANDED_DISTANCE = 250 * MILES_TO_METERS; // 250 miles in meters
const INFINITE_DISTANCE = Number.MAX_SAFE_INTEGER; // No limit
const ZIP_CODE_ZOOM_LEVEL = 10; // 12 is the default zoom level for zip code search

// Add at the top with other globals
window.userLocation = null;
window.userLocationMarker = null;
window.searchDistance = INITIAL_DISTANCE;
window.visibleMarkers = [];

function initializeUserLocation() {
  if (!window.companies) {
    setTimeout(initializeUserLocation, 1);
    return;
  }

  const locateButton = document.getElementById('locate-button');
  const zipCodeInput = document.getElementById('locator-zip-code');
  const lottieAnimationUrl = locateButton.getAttribute('data-lottie-url');
  const lottieContainer = document.getElementById('lottie-container');

  // Initialize Lottie Animation
  const lottieAnimation = lottie.loadAnimation({
    container: lottieContainer,
    renderer: 'svg',
    loop: true,
    autoplay: true,
    path: lottieAnimationUrl,
  });

  // Handle Animation Events
  lottieAnimation.addEventListener('DOMLoaded', function () {
    // console.log('Lottie animation loaded successfully.');
  });

  lottieAnimation.addEventListener('error', function (error) {
    console.error('Lottie Animation Error:', error);
  });

  // Store the animation instance for later use
  locateButton.lottieAnimation = lottieAnimation;

  locateButton.addEventListener('click', () => handleLocateAction('gps'));
  if (zipCodeInput) {
    zipCodeInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (zipCodeInput.value.trim()) handleLocateAction('zip');
      }
    });
  }
}

function showLocatorZipError(message) {
  const errorEl = document.getElementById('locator-zip-error');
  const zipInput = document.getElementById('locator-zip-code');
  if (!errorEl) return;
  errorEl.textContent = message;
  errorEl.hidden = !message;
  if (zipInput) {
    zipInput.setAttribute('aria-invalid', message ? 'true' : 'false');
  }
}

function handleLocateAction(source = 'auto') {
  const zipInput = document.getElementById('locator-zip-code');
  const zipCode = zipInput ? zipInput.value.trim() : '';
  const useGps = source === 'gps' || (source === 'auto' && !zipCode);
  showLocatorZipError('');
  setLoadingState(true);
  if (useGps) {
    getCurrentLocation();
  } else {
    geocodeZipCode(zipCode);
  }
}

async function geocodeZipCode(zipCode) {
  const { Geocoder } = await google.maps.importLibrary('geocoding');
  const geocoder = new Geocoder();

  try {
    const results = await geocoder.geocode({ address: zipCode + ', USA' });
    if (results.results && results.results[0]) {
      const location = results.results[0].geometry.location;
      updateUserLocation(location);
      const selectedZip = document.getElementById('selected-zip');
      if (selectedZip) {
        selectedZip.textContent = zipCode;
        selectedZip.setAttribute('data-selected-zip', zipCode);
      }
    } else {
      throw new Error('No results found for ZIP code');
    }
  } catch (error) {
    console.error('Geocode error:', error);
    handleGeocodeError(error);
  } finally {
    setLoadingState(false);
  }
}

function getCurrentLocation() {
  if (!navigator.geolocation) {
    showLocatorZipError('Location is not available. Enter a ZIP code to search.');
    setLoadingState(false);
    return;
  }

  const onSuccess = (position) => {
    const pos = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
    };
    updateUserLocation(pos);
    setLoadingState(false);
  };

  const onError = (error) => {
    const code = error && error.code;
    const messages = {
      1: 'Location permission denied. Enter a ZIP code to search.',
      2: 'Location unavailable. Enter a ZIP code to search.',
      3: 'Location request timed out. Enter a ZIP code to search.',
    };
    showLocatorZipError(messages[code] || 'Location unavailable. Enter a ZIP code to search.');
    setLoadingState(false);
  };

  const requestPosition = (timeout) => {
    navigator.geolocation.getCurrentPosition(onSuccess, onError, {
      enableHighAccuracy: false,
      timeout,
      maximumAge: Infinity,
    });
  };

  if (navigator.permissions && navigator.permissions.query) {
    navigator.permissions
      .query({ name: 'geolocation' })
      .then((status) => {
        if (status.state === 'denied') {
          onError({ code: 1 });
          return;
        }
        requestPosition(status.state === 'granted' ? 8000 : 20000);
      })
      .catch(() => requestPosition(15000));
  } else {
    requestPosition(15000);
  }
}

async function updateUserLocation(userLocation, zoomLevel = ZIP_CODE_ZOOM_LEVEL) {
  const lat = typeof userLocation.lat === 'function' ? userLocation.lat() : userLocation.lat;
  const lng = typeof userLocation.lng === 'function' ? userLocation.lng() : userLocation.lng;
  userLocation = new google.maps.LatLng(lat, lng);

  // Clear existing user marker
  if (window.userLocationMarker) {
    window.userLocationMarker.setMap(null);
  }

  const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');

  // Store user location globally for distance calculations
  window.userLocation = userLocation;

  // Create new user marker
  const newUserMarker = new AdvancedMarkerElement({
    position: userLocation,
    map: window.map,
    content: createUserMarker(),
  });

  // Store marker in both local and global scope
  userMarker = newUserMarker;
  window.userLocationMarker = newUserMarker;
  newUserMarker.isUserLocation = true;

  // Update distances for all markers
  if (window.markers) {
    window.markers.forEach((marker) => {
      if (marker.isUserLocation) return;

      const markerPosition = marker.position;
      if (markerPosition) {
        const distance = google.maps.geometry.spherical.computeDistanceBetween(userLocation, markerPosition);

        // Update the marker's stored distance
        marker.distance = distance;
      }
    });
  }

  if (typeof filterMarkersByDistance === 'function') {
    filterMarkersByDistance(userLocation);
  }

  // Close dealer list after location is set
  // Check if dealer map is in mobile view
  if (window.innerWidth <= 768) {
    const stickyHandle = document.querySelector('.sticky-handle-wrapper');
    if (stickyHandle) {
      const dealerList = document.querySelector('.dealer-list-wrapper');
      if (dealerList) {
        dealerList.classList.add('closed');
      }
    }
  }

  // Add resize listener to handle desktop/mobile transitions
  window.addEventListener('resize', () => {
    const dealerList = document.querySelector('.dealer-list-wrapper');
    if (window.innerWidth > 768 && dealerList) {
      dealerList.classList.remove('expanded');
      dealerList.classList.remove('closed');
    }
  });
}

function createUserMarker() {
  const markerElement = document.createElement('div');
  const pulseElement = document.createElement('div');
  const innerCircle = document.createElement('div');

  markerElement.style.backgroundColor = '#4285F4';
  markerElement.classList.add('marker');
  markerElement.classList.add('user-marker');
  pulseElement.classList.add('marker-pulse');
  innerCircle.classList.add('inner-circle');
  markerElement.appendChild(pulseElement);
  markerElement.appendChild(innerCircle);

  return markerElement;
}

function filterMarkersByDistance(userLocation) {
  // console.log('=== Location Search Results ===');
  // console.log('Search radius:', window.searchDistance.toFixed(2), 'miles');

  const cardsToShow = [];
  const cardsContainer = document.getElementById('company-cards-container');
  if (!cardsContainer) {
    console.error('Could not find cards container element');
    return;
  }

  const allCards = Array.from(cardsContainer.querySelectorAll('.company-info-card')).filter(
    (card) => card.id !== 'no-exact-match-message'
  );

  // Remove any existing "no results" message first
  const existingNoResults = document.querySelector('.no-exact-matches');
  if (existingNoResults) {
    existingNoResults.remove();
  }

  const markerList = Array.isArray(window.markers) ? window.markers : [];

  markerList.forEach((marker) => {
    if (marker.isUserLocation) return;

    const markerPosition = marker.position;
    if (!markerPosition) return;

    const distance = google.maps.geometry.spherical.computeDistanceBetween(userLocation, markerPosition);

    const matchingCard = Array.from(allCards).find(
      (card) =>
        card.getAttribute('data-card-id') === marker.cardId ||
        (marker.title === 'Super Pacific HQ' && card.getAttribute('data-type') === 'Super Pacific HQ')
    );

    if (matchingCard) {
      cardsToShow.push({
        card: matchingCard,
        distance: distance,
        distanceMiles: (distance * 0.000621371).toFixed(2),
      });
    }

    marker.setMap(window.map);
  });

  window.visibleMarkers = markerList.filter((marker) => !marker.isUserLocation);

  // Sort cards by distance
  cardsToShow.sort((a, b) => a.distance - b.distance);

  // Clear existing cards
  const existingCards = Array.from(allCards);

  // Hide all cards first
  existingCards.forEach((card) => {
    card.style.display = 'none';
  });

  // Show and reorder cards based on distance
  cardsToShow.forEach(({ card, distanceMiles }) => {
    // console.log('=== Card ===');
    // console.log('card', card);
    // console.log('distanceMiles', distanceMiles);
    card.style.display = 'block';
    let distanceLabel = card.querySelector('.distance-label');
    if (!distanceLabel) {
      distanceLabel = document.createElement('div');
      distanceLabel.className = 'distance-label';
      card.appendChild(distanceLabel);
    }
    // Add distance as a data attribute
    card.setAttribute('data-distance', distanceMiles);
    distanceLabel.textContent = `${distanceMiles} miles away`;

    // Move the card to the correct position
    cardsContainer.appendChild(card);
  });

  // Store all card IDs as matching
  const matchingCardIds = cardsToShow.map(({ card }) => card.getAttribute('data-card-id'));

  // Dispatch event with all card IDs
  const event = new CustomEvent('locationSearchComplete', {
    detail: {
      active: true,
      matchingCardIds: matchingCardIds,
      radius: window.searchDistance,
    },
  });

  // console.log('Dispatching event with matches:', matchingCardIds);
  window.dispatchEvent(event);

  // Store on window for direct access
  window.locationSearch = {
    matchingCardIds: matchingCardIds,
    radius: window.searchDistance,
  };

  window.map.setCenter(userLocation);
  window.map.setZoom(ZIP_CODE_ZOOM_LEVEL);
}

// Expand search radius
function updateExpandSearchButton() {
  const expandButton = document.getElementById('expand-search-button');
  const noMatchMessage = document.getElementById('no-exact-match-message');
  const messageText = noMatchMessage?.querySelector('.no-match-text');
  if (!expandButton || !messageText) return;

  if (window.searchDistance === INITIAL_DISTANCE) {
    messageText.textContent = 'No matches found within 100 miles.';
    expandButton.textContent = 'Expand Search to 250 Miles';
    expandButton.style.display = 'block';
  } else if (window.searchDistance === EXPANDED_DISTANCE) {
    messageText.textContent = 'No matches found within 250 miles.';
    expandButton.textContent = 'Show All Locations';
    expandButton.style.display = 'block';
  } else {
    messageText.textContent = 'No matches found.';
    expandButton.style.display = 'none';
  }
}

function handleExpandSearch() {
  if (!window.userLocation) return;

  if (window.searchDistance === INITIAL_DISTANCE) {
    window.searchDistance = EXPANDED_DISTANCE;
  } else if (window.searchDistance === EXPANDED_DISTANCE) {
    window.searchDistance = INFINITE_DISTANCE;
  }

  filterMarkersByDistance(window.userLocation);
}

function updateCardsInMapBounds() {
  if (!window.map || !Array.isArray(window.markers) || !window.markers.length) return;
  const bounds = window.map.getBounds();
  if (!bounds) return;

  if (typeof updateFilters === 'function' && Array.isArray(window.companies) && window.companies.length) {
    updateFilters();
    return;
  }

  const companies = Array.isArray(window.companies) ? window.companies : [];
  companies.forEach((company) => {
    const card = company.element;
    if (!card || card.id === 'no-exact-match-message') return;
    const cardId = card.getAttribute('data-card-id');
    const marker = window.markers.find((item) => item.cardId === cardId);
    const inBounds = !!(marker && marker.position && bounds.contains(marker.position));
    const allowed = company.visible !== false;
    card.style.display = allowed && inBounds ? 'block' : 'none';
  });
}

function refreshMapLayout() {
  if (!window.map) return;
  const mapEl = document.getElementById('dealer-map');
  if (!mapEl) return;
  const rect = mapEl.getBoundingClientRect();
  const next = { w: Math.round(rect.width), h: Math.round(rect.height) };
  if (window._dealerMapSize && window._dealerMapSize.w === next.w && window._dealerMapSize.h === next.h) {
    return;
  }
  window._dealerMapSize = next;
  const center = window.map.getCenter();
  google.maps.event.trigger(window.map, 'resize');
  if (center) window.map.setCenter(center);
}

function fitMapToMarkers(bounds, options = {}) {
  if (!window.map || !bounds || bounds.isEmpty()) return;
  const minZoom = options.minZoom ?? 4;
  const maxZoom = options.maxZoom ?? 18;
  window.map.fitBounds(bounds, 48);
  google.maps.event.addListenerOnce(window.map, 'idle', () => {
    const zoom = window.map.getZoom();
    if (zoom < minZoom) window.map.setZoom(minZoom);
    if (zoom > maxZoom) window.map.setZoom(maxZoom);
  });
}

function handleGeocodeError(error) {
  let errorMessage = 'An error occurred while processing your request.';
  if (error.message === 'No results found for ZIP code') {
    errorMessage = 'No location found for the entered ZIP code. Please check and try again.';
  }
  showLocatorZipError(errorMessage);
}

function setLoadingState(isLoading) {
  const locateButton = document.getElementById('locate-button');
  locateButton.disabled = isLoading;
  const buttonText = document.getElementById('button-text');
  // const buttonFallbackText = document.getElementById('button-fallback-text');
  const lottieContainer = document.getElementById('lottie-container');

  if (isLoading) {
    if (lottieContainer) {
      // Hide SVG and fallback text
      if (buttonText) buttonText.style.display = 'none';
      // if (buttonFallbackText) buttonFallbackText.style.display = 'none';

      lottieContainer.style.display = 'block'; // This line might be the issue
      locateButton.disabled = true;
    } else {
      console.error('Error: Lottie container not found.');
      return;
    }
  } else {
    if (lottieContainer) {
      // Show SVG and hide Lottie animation
      if (buttonText) buttonText.style.display = 'block';
      lottieContainer.style.display = 'none';
      locateButton.disabled = false;
    } else {
      console.error('Error: Lottie container not found.');
      return;
    }
  }
}

// Initialize the user location functionality when the DOM is fully loaded
document.addEventListener('DOMContentLoaded', initializeUserLocation);
